import * as ko from "knockout";
import Bao from "./../services/bao-service";
import TabPage from "../factories/tab-page";
import BaseViewModel from "../../projectx/factories/base-view-model2";

import ConfigurationItemAuditDialog from "../factories/configuration-item-audit-dialog";

/**
 * Contains Base functionalities for ConfigurationItem Extensions
 */
abstract class ConfigurationItemBaseViewModel extends BaseViewModel {

    public constructor(tabPage: TabPage) {
        super(tabPage);
    }

    /**
     * Primary Key of the Configuration Item
     */
    public ConfigurationItemId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Id of the Mandator assinged to the ConfigurationItem
     */
    public MandatorId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Id of the ConfigurationItemType assinged to the ConfigurationItem
     */
    public ConfigurationItemTypeId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the ConfigurationItemType assinged to the ConfigurationItem using ConfigurationItemTypeId.
     */
    public ConfigurationItemTypeName: KnockoutObservable<string> = ko.observable();

    /**
     * Prefetched
     * Key of the ConfigurationItemType assinged to the ConfigurationItem using ConfigurationItemTypeId.
     */
    public ConfigurationItemTypeKey: KnockoutObservable<string> = ko.observable();

    /**
     * Name 1 of the Configuration Item
     */
    public Name1: KnockoutObservable<string> = ko.observable();

    /**
     * Name 2 of the Configuration Item
     */
    public Name2: KnockoutObservable<string> = ko.observable();

    /**
     * Prefetched
     * Key of the ConfigurationItemType assinged to the ConfigurationItem using ConfigurationItemTypeId.
     */
    public Number: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the Manufacturer assinged to the ConfigurationItem
     */
    public ManufacturerId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the Manufacturer assinged to the ConfigurationItem using ManufacturerId
     */
    public ManufacturerName: KnockoutObservable<string> = ko.observable();

    /**
     * Prefetched
     * Id of the ManufacturerType of the Manufacturer using ManufacturerId
     */
    public ManufacturerTypeId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the ManufacturerType of the Manufacturer using ManufacturerId
     */
    public ManufacturerTypeName: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the ConfigurationItemStatus assinged to the ConfigurationItem
     */
    public ConfigurationItemStatusId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the ConfigurationItemStatus assinged to the ConfigurationItem using ConfigurationItemStatusId
     */
    public ConfigurationItemStatusName: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the ConfigurationItemIndicator assinged to the ConfigurationItem
     */
    public IndicatorId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     *  Name of the ConfigurationItemStatus assinged to the ConfigurationItem using IndicatorId
     */
    public ConfigurationItemIndicatorName: KnockoutObservable<string> = ko.observable();

    /**
     * Flag that shows if the Configuration Item is marked as VIP
     */
    public Vip: KnockoutObservable<boolean> | null = ko.observable();

    /**
     * Serial Number of the ConfigurationItem
     */
    public SerialNumber: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the Parent ConfigurationItem
     * Configuration items can be organised into a tree like structure and this is the parent 
     */
    public ParentConfigurationItemId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the Parent ConfigurationItem assinged to the ConfigurationItem using ParentConfigurationItemId
     */
    public ParentConfigurationItemName: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the Owner
     */
    public OwnerId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Id of the Customer
     */
    public CustomerId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the Customer assinged to the ConfigurationItem using CustomerId
     */
    public CustomerName: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the Country assinged to the ConfigurationItem
     */
    public CountryId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the Country assinged to the ConfigurationItem <see cref="CountryId"/>
     */
    public CountryName: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the Location assinged to the ConfigurationItem
     */
    public LocationId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the Location assinged to the ConfigurationItem <see cref="CountryId"/>
     */
    public LocationName: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the Building assinged to the ConfigurationItem
     */
    public BuildingId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the Building assinged to the ConfigurationItem <see cref="BuildingId"/>
     */
    public BuildingName: KnockoutObservable<string> = ko.observable();

    /**
     * Name of the Room assinged to the Configuration Item
     */
    public Room: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the Department assinged to the ConfigurationItem
     */
    public DepartmentId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the DepartmentName assinged to the ConfigurationItem <see cref="DepartmentId"/>
     */
    public DepartmentName: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the AccountAssignmentType assinged to the ConfigurationItem
     */
    public AccountAssignmentTypeId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the AccountAssignmentTypeName assinged to the AccountAssignmentTypeId <see cref="AccountAssignmentTypeId"/>
     */
    public AccountAssignmentTypeName: KnockoutObservable<string> = ko.observable();

    /**
     * Name of the Account Assignment
     */
    public AccountAssignment: KnockoutObservable<string> = ko.observable();

    /**
     * Remark text of the Configuration Item
     */
    public Remark: KnockoutObservable<string> = ko.observable();

    /**
     * Flag that shows if there is a alternate owner
     */
    public AlternateOwner: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the customer of the owner should be used as customer <see cref="OwnerId"/>
     */
    public UseOwnerCustomer: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the customer of the parent should be used as customer <see cref="ParentConfigurationItemId"/>
     */
    public UseParentCustomer: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the location of the owner should be used as location <see cref="OwnerId"/>
     */
    public UseOwnerLocation: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the location of the parent should be used as location <see cref="ParentConfigurationItemId"/>
     */
    public UseParentLocation: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the account assignment of the owner should be used as account assignment <see cref="OwnerId"/>
     */
    public UseOwnerAccountAssignment: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the account assignment of the parent should be used as account assignment <see cref="ParentConfigurationItemId"/>
     */
    public UseParentAccountAssignment: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the department of the owner should be used as department <see cref="OwnerId"/>
     */
    public UseOwnerDepartment: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the department of the parent should be used as department <see cref="ParentConfigurationItemId"/>
     */
    public UseParentDepartment: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the building of the owner should be used as building <see cref="OwnerId"/>
     */
    public UseOwnerBuilding: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the building of the parent should be used as building <see cref="ParentConfigurationItemId"/>
     */
    public UseParentBuilding: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the room of the owner should be used as room <see cref="OwnerId"/>
     */
    public UseOwnerRoom: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the room of the parent should be used as room <see cref="ParentConfigurationItemId"/>
     */
    public UseParentRoom: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the country of the owner should be used as country <see cref="OwnerId"/>
     */
    public UseOwnerCountry: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the country of the parent should be used as country <see cref="ParentConfigurationItemId"/>
     */
    public UseParentCountry: KnockoutObservable<boolean> = ko.observable();

    /**
     * Id of the State assinged to the ConfigurationItem
     */
    public StateId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the State assinged to the ConfigurationItem <see cref="StateId"/>
     */
    public StateName: KnockoutObservable<string> = ko.observable();

    /**
     * Full Name of the Supervisor Person
     */
    public SupervisorPersonFullName: KnockoutObservable<string> = ko.observable();

    /**
     * Id of the Advisor Person assinged to the ConfigurationItem
     */
    public AdvisorPersonId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the Advisor Person assinged to the ConfigurationItem <see cref="AdvisorPersonId"/>
     */
    public AdvisorPersonName: KnockoutObservable<string> = ko.observable();

    /**
     * Flag that shows if the state of the owner should be used as state <see cref="OwnerId"/>
     */
    public UseOwnerState: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the state of the parent should be used as state <see cref="ParentConfigurationItemId"/>
     */
    public UseParentState: KnockoutObservable<boolean> = ko.observable();

    /**
     * Flag that shows if the AdvisorPerson of the parent should be used as AdvisorPerson <see cref="ParentConfigurationItemId"/>
     */
    public UseParentAdvisorPerson: KnockoutObservable<boolean> = ko.observable();

    /**
     * Id of the Original ConfigurationItem
     */
    public OriginalConfigurationItemId: KnockoutObservable<string> | null = ko.observable();

    /**
     * Prefetched
     * Name of the Parent ConfigurationItem <see cref="OriginalConfigurationItemId"/>
     */
    public OriginalConfigurationItemName: KnockoutObservable<string> = ko.observable();

    /**
     * Prefethed
     * Id of the Type of the Parent ConfigurationItem <see cref="OriginalConfigurationItemId"/>
     */
    public OriginalConfigurationItemTypeId: KnockoutObservable<string> | null = ko.observable();


    /**
     * Flag that shows if the configuration item is a referance object
     */
    public IsReferenceObject: KnockoutObservable<boolean> = ko.observable();

    /**
     * Url that points to the definition of the configuration item type
     */
    public SettingsUrl: KnockoutObservable<string> = ko.computed(() => { return this.ConfigurationItemTypeId() ? 'ConfigurationItemType/' + this.ConfigurationItemTypeId() : null })

    /**
     * Permission key for the audit trail.
     */
    public AuditTrailKeyPermission: KnockoutObservable<string> = ko.observable(null);

    /**
     * Url used in the GetTabsByConfigurationItemTypeId method to load the tabs assinged to the ConfigurationItemType
     */
    public GetTabsByConfigurationItemTypeIdUrl: KnockoutObservable<string> = ko.observable("ConfigurationItem/GetTabsByConfigurationItemTypeId");

    /**
     * Bao Searcher for selecting the ConfigurationItemType
     */
    public ConfigurationItemTypeSearcher: Bindings.baoSearcher = {
        SearchType: "ConfigurationItemType",
        ObjectId: this.ConfigurationItemTypeId,
        ObjectValue: "ConfigurationItemType",
        AutoComplete: true,
        AutoSearch: true,
    };

    /**
     * Bao Searcher for selecting the ConfigurationItemStatus
     */
    public ConfigurationItemStatusSearcher: Bindings.baoSearcher = {
        SearchType: "ConfigurationItemStatus",
        ObjectId: this.ConfigurationItemStatusId,
        ObjectValue: "TabName",
        AutoComplete: true,
        AutoSearch: true,
    };

    /**
     * Gets the tabs assinged to the ConfigurationItemType and adds them to the TabController
     */
    public GetTabsByConfigurationItemTypeId = () => {
        Bao.post(this.GetTabsByConfigurationItemTypeIdUrl(), JSON.stringify({ configurationItemTypeId: this.ConfigurationItemTypeId() }), (result) => {
            this.GetTabsByConfigurationItemTypeIdResultHandler(result);
        })
    }

    public GetTabsByConfigurationItemTypeIdResultHandler(result) {
        this.TabPage.DirtyFlag.pause();

        $.each(ko.map.toJS(result), (index, item) => {
            if (item.PreLoad == null) {
                item.PreLoad = false;
            }
            let newTab = new TabPage(null, item.Caption);
            newTab.AutoSelect = false;
            newTab.Url(item.WebUrl);
            newTab.IsClosable(false);
            newTab.PreLoad(item.PreLoad);
            this.TabController.AddTab(newTab);
        });

        if (this.TabController.Tabs().length > 0) {
            this.TabController.SelectTab(this.TabController.Tabs()[0]);
        }

        this.TabPage.DirtyFlag.resume();
    }

    public init() {
        let def = $.Deferred<void>();

        super.init().then(() => {
            super.InitTabController().then(() => {
                this.GetTabsByConfigurationItemTypeId();
                def.resolve();
            })
        });

        this.subscribe(this.ConfigurationItemTypeId, () => {
            if (this.Initialized()) {
                this.GetTabsByConfigurationItemTypeId();
            }
        });

        return def.promise();
    }

    /**
     * Gets called from super.Save() and saves the ConfigurationItemTabs.
     * @param response The response from super.Save() post request.
     */
    public HandleSaveResponse(response): JQueryPromise<void> {
        let def = $.Deferred<void>();
        this.SaveTime(response.Result);

        if (this.TabController != null) {
            this.TabController.SaveTabs(response.Result).then(() => {
                this.PerformRefresh(response.Model).then(() => {
                    def.resolve();
                });
            });
        }
        else {
            this.PerformRefresh(response.Model).then(() => {
                def.resolve();
            });
        }

        Bao.raiseEvent('user-tagging:save', {});
        return def.promise();
    }

    /**
     * Is called when menu button other than save, refresh, delete, qr-code is clicked. 
     * @param key Action key from a menu button.
     */
    public CustomToolbarClick(key: string) {
        if (key == this.AuditTrailKeyPermission()) {
            this.AuditTrail();
        }
    }

    /**
     * Opens a dialog with the audit trail for the current configuration item.
     */
    public AuditTrail() {
        let auditDialog = new ConfigurationItemAuditDialog(this.ConfigurationItemId(), ko.unwrap(this.TabPage.Title), this.TabPage);
        auditDialog.Open();
    }
}
export default ConfigurationItemBaseViewModel;